import { useState } from "react";

import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { RECENT_DEFAULT_SORT } from "@/modules/docs/fields";
import { runLocalQuery } from "@/modules/docs/localQuery";
import { projectDocRow } from "@/modules/docs/projections";
import type { DocRow } from "@/modules/docs/types";
import { useDocsPeople } from "@/modules/docs/useDocsPeople";
import { useDocsTree } from "@/modules/docs/useDocsTree";
import type { SectionScreenProps } from "@/modules/types";

/**
 * Recently changed pages, newest first.
 *
 * `DocsRepository` has no "every page across every folder" method — see the
 * comment on `useDocsTree` — so this builds the row set on the client the
 * same way `orders/screens/ChangesList.tsx` does for order history, and
 * runs it through the same filter bar every other list offers rather than
 * shipping a bespoke one (CLAUDE.md: "a module that ships its own filter UI
 * is a review rejection").
 */
export function Recent({
  fields,
  filter,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const treeQuery = useDocsTree();
  const people = useDocsPeople();

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];

  if (treeQuery.isPending || people.isPending) return <TableSkeleton />;

  if (treeQuery.isError) {
    return (
      <LoadFailed
        message={treeQuery.error.message}
        onRetry={() => {
          void treeQuery.refetch();
        }}
      />
    );
  }

  const { folders, allPages, contentByPageId } = treeQuery.data;
  const folderById = new Map(folders.map((folder) => [folder.id, folder]));

  const rows: DocRow[] = allPages.map((page) => {
    const content = contentByPageId.get(page.id);
    return {
      page,
      folder: folderById.get(page.folderId),
      content,
      authorName: content
        ? people.principalName("user", content.revision.authorUserId)
        : "",
    };
  });

  const result = runLocalQuery(
    rows,
    { filter, sort, limit, cursor },
    {
      project: projectDocRow,
      identify: (row) => row.page.id,
      defaultSort: RECENT_DEFAULT_SORT,
    },
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <DataTable
        fields={fields}
        rows={result.items}
        getRowId={(row) => row.page.id}
        project={projectDocRow}
        sort={sort}
        onToggleSort={onToggleSort}
        emptyState={<NoMatches onClear={onClearFilter} />}
        footer={
          <Pagination
            page={result}
            limit={limit}
            onLimitChange={(next) => {
              setLimit(next);
              setHistory([undefined]);
            }}
            history={history}
            onNavigate={setHistory}
          />
        }
      />
    </div>
  );
}
